import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ReferralStatusBadge } from "@/components/referrals/ReferralStatusBadge";
import { type ReferralUIData } from "@/lib/schema";
import { formatRewardAmount, formatDateForSouthAfrica } from "@/lib/textUtils";

interface ReferralTableItemProps {
  referral: ReferralUIData;
  index: number;
  onViewReferral: (referral: ReferralUIData) => void;
}

export function ReferralTableItem({ referral, index, onViewReferral }: ReferralTableItemProps) {
  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <div className="lg:grid lg:grid-cols-6 lg:gap-4 p-4 hover:bg-gray-50 transition-colors">
      {/* Desktop Layout */}
      <div className="hidden lg:flex items-center gap-3">
        <Avatar className="h-10 w-10">
          <AvatarImage src={referral.candidateAvatar} />
          <AvatarFallback className="bg-gray-200 text-gray-600 text-sm">
            {getInitials(referral.candidateName)}
          </AvatarFallback>
        </Avatar>
        <span className="font-medium text-gray-900 text-sm leading-[140%] tracking-normal">{referral.candidateName}</span>
      </div>

      <div className="hidden lg:flex items-center">
        <span className="text-gray-900 font-medium text-sm leading-[140%] tracking-normal">{referral.jobTitle}</span>
      </div>

      <div className="hidden lg:flex items-center">
        <ReferralStatusBadge status={referral.status} />
      </div>

      <div className="hidden lg:flex items-center">
        <span className="text-gray-600 font-medium text-sm leading-[140%] tracking-normal">{formatDateForSouthAfrica(referral.referralDate)}</span>
      </div>

      <div className="hidden lg:flex items-center">
        <span className="text-gray-600 font-medium text-sm leading-[140%] tracking-normal">R{formatRewardAmount(referral.reward)}</span>
      </div>

      <div className="hidden lg:flex items-center">
        <Button 
          variant="secondary" 
          size="sm" 
          onClick={() => onViewReferral(referral)}
          className="space-grotesk font-medium"
        >
          VIEW
        </Button>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={referral.candidateAvatar} />
              <AvatarFallback className="bg-gray-200 text-gray-600 text-sm">
                {getInitials(referral.candidateName)}
              </AvatarFallback>
            </Avatar>
            <div>
              <span className="font-medium text-gray-900 text-sm leading-[140%] tracking-normal block" style={{ fontFamily: 'Manrope' }}>{referral.candidateName}</span>
              <span className="text-gray-600 font-medium text-sm leading-[140%] tracking-normal" style={{ fontFamily: 'Manrope' }}>{referral.jobTitle}</span>
            </div>
          </div>
          <Button 
            variant="secondary" 
            size="md" 
            onClick={() => onViewReferral(referral)}
            className="space-grotesk font-medium"
          >
            VIEW
          </Button>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <ReferralStatusBadge status={referral.status} />
            <span className="text-gray-500 font-medium text-sm leading-[140%] tracking-normal" style={{ fontFamily: 'Manrope' }}>{formatDateForSouthAfrica(referral.referralDate)}</span>
          </div>
          <span className="font-semibold text-gray-900 space-grotesk">R{formatRewardAmount(referral.reward)}</span>
        </div>
      </div>
    </div>
  );
}
